import React, { useState, useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import { SupportApiService } from "../../Service/SupportApiService";
import Spinner from "../Common/Spinner";
import { CommonService } from "../../Service/CommonService";

interface Barrel {
  barrelId: string;
  barrelNumber: string;
  valuation: number;
  barrelCost: number;
}

interface Lot {
  productName: string;
  lotNo: string;
  quantity: number;
  barrelNumbers: Barrel[];
}

interface ChildProps {
  customerServiceRequestId: string;
  reloadTable?: () => void;
  onClose: () => void;
}

const ManageBarrelValuationDetails: React.FC<ChildProps> = ({
  customerServiceRequestId,
  onClose,
}) => {
  const [show, setShow] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isGettingData, setIsGettingData] = useState(false);
  const [lots, setLots] = useState<Lot[]>([]);
  const [selectedLot, setSelectedLot] = useState<Lot | null>(null);
  const [editedValuations, setEditedValuations] = useState<Record<string, number>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [applyToAll, setApplyToAll] = useState(false);
  const [applyValue, setApplyValue] = useState<number | "">("");

  const handleClose = () => {
    setEditedValuations({});
    setApplyToAll(false);
    setApplyValue("");
    onClose();
  };

  // Load data (API / mock)
  const fetchValuationDetails = async () => {
    setIsGettingData(true);

    try {
      const response = await SupportApiService.fetchBarrelValuationDetails(customerServiceRequestId);

      const data = response?.data || [];
      setLots(data);
      setSelectedLot(data[0] || null);

      // Initialize editedValuations
      const initialValuations: Record<string, number> = {};
      data.forEach((lot: Lot) => {
        lot.barrelNumbers.forEach((barrel) => {
          initialValuations[barrel.barrelId] = barrel.barrelCost || 0;
        });
      });
      setEditedValuations(initialValuations);
    } catch (error) {
      console.log("Error occurred while getting barrel valuation details " + error);
    } finally {
      setIsGettingData(false);
    }
  };

  // Handle valuation input per container
  const handleValuationChange = (barrelId: string, value: string) => {
    const numericValue = parseFloat(value);
    setEditedValuations((prev) => ({ ...prev, [barrelId]: numericValue }));

    // remove validation error
    setValidationErrors((prev) => {
      const updated = { ...prev };
      if (numericValue && numericValue > 0) delete updated[barrelId];
      return updated;
    });
  };

  // Apply same valuation to all Container in the selected lot
  const handleApplyToAll = () => {
    if (!applyValue || Number(applyValue) <= 0) {
      CommonService.Toast.fire({
        title: "Please Enter a Valid Valuation Cost Before Applying.",
        icon: "warning",
      });
      return;
    }

    const updatedValuations = { ...editedValuations };
    const updatedErrors = { ...validationErrors };

    // Apply only to selected lot
    selectedLot.barrelNumbers.forEach((barrel) => {
      updatedValuations[barrel.barrelId] = Number(applyValue);

      // remove existing validation error
      if (updatedErrors[barrel.barrelId]) {
        delete updatedErrors[barrel.barrelId];
      }
    });

    setEditedValuations(updatedValuations);
    setValidationErrors(updatedErrors);
    setApplyValue(""); // clear input after apply

    CommonService.Toast.fire({
      title: `Valuation Cost Applied to Selected Lot (${selectedLot.lotNo})`,
      icon: "success",
    });
  };
  
  // Submit updated valuations
  const handleSubmit = async () => {
    const errors: Record<string, string> = {};

    lots.forEach((lot) => {
      lot.barrelNumbers.forEach((barrel) => {
        const valuationValue = editedValuations[barrel.barrelId];
        if (valuationValue === undefined ||
          valuationValue === null ||
          valuationValue <= 0 ||
          isNaN(valuationValue)
        ) {
          errors[barrel.barrelId] = "Please enter valid Valuation Cost";
        }
      });
    });

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const updatedBarrels: {
      barrelId: string;
      barrelNumber: string;
      valuation: number;
    }[] = [];

    lots.forEach((lot) => {
      lot.barrelNumbers.forEach((barrel) => {
        const newValuation = editedValuations[barrel.barrelId];
        if (newValuation !== undefined && newValuation !== barrel.barrelCost) {
          updatedBarrels.push({
            barrelId: barrel.barrelId,
            barrelNumber: barrel.barrelNumber,
            valuation: newValuation,
          });
        }
      });
    });

    if (updatedBarrels.length === 0) {
      CommonService.Toast.fire({
        title: "No changes detected in Valuation Cost",
        icon: "info",
      });
      return;
    }

    setIsLoading(true);

    try {
      await SupportApiService.updateBarrelValuationDetails(updatedBarrels);
      CommonService.Toast.fire({
        title: "Valuation Cost updated successfully",
        icon: "success",
      });

      handleClose();
    } catch (error) {
      console.log("Error while updating barrel valuation:", error);
      CommonService.Toast.fire({
        title: "Failed to update valuation",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchValuationDetails();
    }
  }, [show]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="modal__wd modal__wd--submitSellReq"
      size="lg"
    >
      <Modal.Header className="proofing-modal-header">
        <Modal.Title className="proofing-modal-title">
          Manage Container Valuation
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="proofing-modal-body">
        {isGettingData ? (
          <div className="text-center py-4">
            <Spinner />
            <p>Loading valuation details...</p>
          </div>
        ) : (
          <div className="proofing-modal-inner">
            <div className="row g-0">
              {/* Left Panel */}
              <div className="col-md-5 col-lg-4">
                <div className="list-wrapper">
                  {lots.map((lot) => (
                    <div
                      key={lot.lotNo}
                      onClick={() => setSelectedLot(lot)}
                      className={`list-item-wrapper ${
                        selectedLot?.lotNo === lot.lotNo ? "active" : ""
                      }`}
                      style={{
                        backgroundColor:
                          selectedLot?.lotNo === lot.lotNo ? "#fff" : "#f0f0f0",
                      }}
                    >
                      <div className="d-flex justify-content-between gap-1 w-100 align-items-center flex-wrap">
                        <div>
                          <p className="list-inventory-title">
                            {lot.productName}
                          </p>
                          <p className="list-lot-no">Lot ID : {lot.lotNo}</p>
                        </div>
                        <div className="text-end ms-auto text-nowrap">
                          <span className="own-barrels ms-2">
                            {lot.barrelNumbers.length}{" "}
                            {lot.barrelNumbers.length > 1
                              ? "Containers"
                              : "Container"}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Panel */}
              <div className="col-md-7 col-lg-8">
                  <div className="lot-details flex-grow-1 ps-3">
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-3 flex-wrap">
                        <input
                          type="number"
                          min="0"
                          placeholder="Enter Valuation Cost"
                          value={applyValue}
                          onChange={(e) => {
                            const value = e.target.value;
                            // prevent negative numbers or invalid input
                            if (value === "" || Number(value) >= 0) {
                              setApplyValue(value ? Number(value) : "");
                            }
                          }}
                          style={{minWidth:"221px", width:"unset"}}
                          className="proofing-input all-valution-input"
                        />
                        <button className="secondary-btn ms-auto" style={{minWidth:"190px"}} onClick={handleApplyToAll} >
                        Apply Valuation
                      </button>
                    </div>
                    {selectedLot ? (
                      <>
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Container Id</th>
                              <th>Valuation Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedLot.barrelNumbers.map((barrel) => (
                              <tr key={barrel.barrelId}>
                                <td>
                                  <p>{barrel.barrelNumber}
                                    </p>
                                  </td>
                                <td>
                                  <div className="position-relative">
                                  <input
                                    type="number"
                                    min="0"
                                    value={editedValuations[barrel.barrelId] ?? ""}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (value === "" || Number(value) >= 0) {
                                        handleValuationChange(barrel.barrelId, value);
                                      }
                                    }}
                                    className="proofing-input" placeholder="Enter Valuation Cost"
                                  />
                                  {validationErrors[barrel.barrelId] && (
                                    <span className="error-msg">
                                      {validationErrors[barrel.barrelId]}
                                    </span>
                                  )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    ) : (
                      <div className="text-muted">
                        Select a lot to view container details
                      </div>
                    )}
                  </div>
                </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-center pt-0">
        <div className="btn-group d-flex justify-content-center gap-2 w-100 proofing-modal-footer">
            <button
            style={{minWidth:"190px"}}
              className="secondary-btn "
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
            style={{minWidth:"190px"}}
              className="primary-btn "
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading && <Spinner />} Add Details
            </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ManageBarrelValuationDetails;
